import { ChangeDetectionStrategy, Component, OnInit, computed, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { debounceTime, distinctUntilChanged, filter, startWith, tap } from 'rxjs';
import { MatRippleModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { IPagination } from 'app/core/http';
import { DrawerListDetailComponent } from 'app/shared/components';
import { MatDrawer } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { needConfirmation, SnackbarMsgService } from 'app/shared';
import { ApplicationsStateService } from '../applications.state.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { trigger, style, transition, animate, keyframes } from '@angular/animations';
import { PaginationListComponent } from 'app/shared/components/pagination/pagination-list.component';
import { PaginationService } from 'app/shared/components/pagination/pagination.service';
import { ResourceParameters } from 'app/shared';
import { ApplicationsLoadingEnum } from '../@shared/enums';

@Component({
    selector: 'list-applications',
    templateUrl: './list-applications.component.html',
    standalone: true,
    imports: [
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatDividerModule,
        MatTableModule,
        MatSortModule,
        MatRippleModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatInputModule,
        DrawerListDetailComponent,
        MatProgressSpinnerModule,
        MatTooltipModule,
        PaginationListComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('rotate', [
            transition('* => loading', [
                animate('2s linear', keyframes([
                    style({ transform: 'rotate(0)', offset: 0 }),
                    style({ transform: 'rotate(360deg)', offset: 1 })
                ]))
            ]),
            transition('loading => *', [
                animate('0s')
            ])
        ])
    ]
})
export class ListApplicationsComponent implements OnInit {
    applications = this._applicationsState.applications;
    loading = this._applicationsState.loadingState;
    readonly totalCount = computed(() => this.applications().length);

    searchInputControl = new FormControl('');
    matDrawer = signal<MatDrawer|null>(null);
    loadingEnum = ApplicationsLoadingEnum;

    constructor(
        private readonly _router: Router,
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _applicationsState: ApplicationsStateService,
        private readonly _paginationService: PaginationService,
        private readonly _snackbarMsgService: SnackbarMsgService
    ) { }

    ngOnInit() {
        this.setupSearch();
    }

    ngOnDestroy() {
        this._paginationService.reset();
    }

    private setupSearch() {
        this.searchInputControl.valueChanges.pipe(
            debounceTime(700),
            distinctUntilChanged(),
            startWith(''),
            filter(() => this.searchInputControl.enabled),
            tap(() => {
                const params: ResourceParameters = {
                    pageNumber: 1,
                    pageSize: this._paginationService.pagination().pageSize,
                    searchQuery: this.searchInputControl.value || ''
                };
                
                this.loadApplicationsWithParams(params);
            })
        ).subscribe();
    }

    private loadApplicationsWithParams(params: ResourceParameters) {
        this._applicationsState.loadApplicationsWithSearch(
            params,
            ApplicationsLoadingEnum.Loading
        ).subscribe();
    }

    loadApplications(pagination?: IPagination) {
        const params: ResourceParameters = {
            pageSize: pagination?.pageSize || 10,
            pageNumber: pagination?.currentPage || 1,
            searchQuery: this.searchInputControl.value || ''
        };

        this.loadApplicationsWithParams(params);
    }

    onRefreshClick() {
        const params = {
            pageNumber: this._paginationService.pagination().currentPage,
            pageSize: this._paginationService.pagination().pageSize,
            searchQuery: this.searchInputControl.value || ''
        };

        this._applicationsState.loadApplicationsWithSearch(params, ApplicationsLoadingEnum.Refreshing).subscribe();
    }

    onAddApplicationClick($event: Event) {
        $event.preventDefault();
        $event.stopPropagation();
        this._router.navigate(['add'], { relativeTo: this._activatedRoute });
    }

    onEditApplicationClick(id: string) {
        this._router.navigate([id], { relativeTo: this._activatedRoute });
    }

    @needConfirmation({
        title: 'Deletar aplicação',
        message: 'Tem certeza que deseja deletar esta aplicação?',
        type: 'warn'
    })
    onDeleteApplicationClick(id: string) {
        this._applicationsState.removeApplication(id).pipe(
            tap(() => {
                if (this._router.url.includes(id)) {
                    this._router.navigate(['.'], { relativeTo: this._activatedRoute });
                }
                this._snackbarMsgService.success('Aplicação removida com sucesso', 'Aplicação');
            })
        ).subscribe();
    }

    onReceiveDrawer($event: MatDrawer) {
        this.matDrawer.set($event);
    }
} 